import React, { useEffect } from "react";
import Navbar from "../components/navbar";
import Footer from "../components/footer";

const copy = {
  pt: {
    hero: {
      badge: "Projeto PRR",
      title: "Coaching 4.0",
      subtitle: "Apoio a Modelos de Negócio para a Transição Digital",
      body: "Integração de tecnologia na PME, com o objetivo de desenvolver processos e competências organizacionais que fomentem a transformação digital do modelo de negócio da PME.",
    },
    projectInfo: {
      title: "Detalhes do Projeto",
      items: [
        { label: "Designação do projeto", value: "Apoio a Modelos de Negócio para a Transição Digital – Coaching 4.0" },
        { label: "N.º do projeto", value: "18749" },
        { label: "Objetivo principal", value: "Integração de tecnologia na PME, com o objetivo de desenvolver processos e competências organizacionais que fomentem a transformação digital do modelo de negócio da PME" },
        { label: "Região de intervenção", value: "Norte" },
        { label: "Entidade Beneficiária", value: "Invisible Cloud Lda" },
      ],
      timeline: [
        { label: "Data de Aprovação", value: "02/07/2025", icon: "check" },
        { label: "Data de início", value: "05/01/2026", icon: "play" },
        { label: "Data de conclusão", value: "30/06/2026", icon: "flag" },
      ],
      funding: [
        { label: "Custo total elegível", value: "10.000€" },
        { label: "Apoio financeiro da União Europeia", value: "10.000€" },
      ],
    },
    objectives: {
      title: "Objetivos, atividades e resultados esperados/atingidos",
      items: [
        {
          desc: "Realização de diagnósticos da maturidade digital da PME e das competências digitais dos seus trabalhadores e gestores.",
        },
        {
          desc: "Desenvolvimento de competências e processos que permitam a adoção de tecnologias avançadas e a digitalização das cadeias de valor.",
        },
        {
          desc: "Sensibilização e capacitação de trabalhadores e empresários para os desafios da Transição Digital",
        },
        {
          desc: "Aquisição de serviços integrados no Catálogo de Serviços de Transição Digital, na área de Relação com o Cliente (Estratégia de Marketing e Comunicação), com vista à transformação digital do modelo de negócio.",
        },
      ],
    },
    funding: {
      title: "Apoio Institucional",
      description: "Projeto apoiado no âmbito do Plano de Recuperação e Resiliência",
      linkLabel: "Visitar recuperarportugal.gov.pt",
      linkUrl: "https://www.recuperarportugal.gov.pt",
    },
  },
  en: {
    hero: {
      badge: "PRR Project",
      title: "Coaching 4.0",
      subtitle: "Support for Business Models for Digital Transition",
      body: "Technology integration in SMEs, aiming to develop organizational processes and competencies that foster the digital transformation of the SME's business model.",
    },
    projectInfo: {
      title: "Project Details",
      items: [
        { label: "Project designation", value: "Support for Business Models for Digital Transition – Coaching 4.0" },
        { label: "Project number", value: "18749" },
        { label: "Main objective", value: "Technology integration in SMEs, aiming to develop organizational processes and competencies that foster the digital transformation of the SME's business model" },
        { label: "Intervention region", value: "Norte" },
        { label: "Beneficiary entity", value: "Invisible Cloud Lda" },
      ],
      timeline: [
        { label: "Approval date", value: "02/07/2025", icon: "check" },
        { label: "Start date", value: "05/01/2026", icon: "play" },
        { label: "Completion date", value: "30/06/2026", icon: "flag" },
      ],
      funding: [
        { label: "Total eligible cost", value: "€10,000" },
        { label: "European Union financial support", value: "€10,000" },
      ],
    },
    objectives: {
      title: "Objectives, activities and expected/achieved results",
      items: [
        {
          desc: "Conducting digital maturity diagnostics of the SME and the digital competencies of its workers and managers.",
        },
        {
          desc: "Development of competencies and processes that enable the adoption of advanced technologies and the digitalization of value chains.",
        },
        {
          desc: "Awareness-raising and capacity-building of workers and entrepreneurs for the challenges of Digital Transition.",
        },
        {
          desc: "Acquisition of integrated services in the Digital Transition Services Catalogue, in the area of Customer Relations (Marketing and Communication Strategy), with a view to the digital transformation of the business model.",
        },
      ],
    },
    funding: {
      title: "Institutional Support",
      description: "Project supported under the Recovery and Resilience Plan",
      linkLabel: "Visit recuperarportugal.gov.pt",
      linkUrl: "https://www.recuperarportugal.gov.pt",
    },
  },
};

export default function Coaching40({ lang = "pt", onLanguageChange }) {
  const content = copy[lang] || copy.pt;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -5% 0px",
      }
    );

    const reveals = document.querySelectorAll(".reveal");
    reveals.forEach((el) => {
      if (!el.classList.contains("reveal-visible")) {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [lang]);

  return (
    <>
      <Navbar activeLang={lang} onLanguageChange={onLanguageChange} />
      <main className="coaching40-page bg-[#F6FBFA] overflow-x-hidden">
        {/* Hero Section - Full viewport height */}
        <section className="relative overflow-hidden bg-white">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(76,177,161,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(31,132,131,0.10),transparent_50%)]" />
          <div className="absolute top-20 right-10 h-72 w-72 rounded-full bg-[#E4F4F0]/60 blur-3xl" />
          <div className="absolute bottom-10 left-10 h-56 w-56 rounded-full bg-[#D5ECE6]/50 blur-3xl" />
          
          <div
            className="
              relative mx-auto flex w-full max-w-6xl flex-col items-center justify-center gap-8
              px-4 py-12 text-center sm:px-6 md:py-16
              min-h-[68svh] sm:min-h-[64svh] md:min-h-[58svh] xl:min-h-screen
              translate-y-0 xl:-translate-y-[7vh]
              lg:px-0
            "
          >
            <div
              className="w-full max-w-3xl space-y-6 reveal"
              data-reveal="fade-up"
            >
              <span 
                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase bg-[#E4F4F0] text-[#1F8483]"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                {content.hero.badge}
              </span>
              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-[#0D2D2A] sm:text-5xl lg:text-6xl">
                {content.hero.title}
              </h1>
              <p className="text-lg font-medium tracking-wide uppercase text-[#1F8483]">
                {content.hero.subtitle}
              </p>
              <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#2F3B3A]">
                {content.hero.body}
              </p>
            </div>
            
            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={() => document.getElementById("coaching40-details")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-14 hidden flex-col items-center rounded-full bg-white/90 px-3 py-4 text-[#1F8483] shadow-[0_16px_40px_-30px_rgba(12,66,61,0.6)] transition hover:bg-white md:flex"
                aria-label={lang === "pt" ? "Rolar para ver mais" : "Scroll to explore"}
              >
                <span className="text-xs font-semibold uppercase tracking-[0.32em] text-[#1F8483]">
                  {lang === "pt" ? "Explorar" : "Explore"}
                </span>
                <svg className="mt-3 h-6 w-6 scroll-indicator" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a.75.75 0 0 1 .75.75v12.19l2.72-2.72a.75.75 0 0 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 1 1 1.06-1.06l2.72 2.72V2.75A.75.75 0 0 1 10 2Z" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        {/* Project Info Section - Simple table layout */}
        <section id="coaching40-details" className="relative bg-white py-16">
          <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-[420px] rounded-[48px] bg-[radial-gradient(circle_at_center,rgba(76,177,161,0.10),rgba(76,177,161,0)_70%)] blur-xl md:block" />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-0">
            <div className="reveal" data-reveal="fade-up">
              <h2 className="text-3xl font-bold text-[#0D2D2A] sm:text-4xl">
                {content.projectInfo.title}
              </h2>
              <div className="mt-8 overflow-hidden rounded-[28px] border border-[#D5ECE6] bg-white shadow-[0_32px_70px_-60px_rgba(12,66,61,0.55)]">
                <div className="divide-y divide-[#E8F4F1]">
                  {[
                    ...content.projectInfo.items,
                    ...content.projectInfo.timeline.map(t => ({ label: t.label, value: t.value })),
                    ...content.projectInfo.funding,
                  ].map((item, index) => (
                    <div
                      key={item.label}
                      className={`grid gap-2 px-6 py-5 transition-colors hover:bg-[#F8FCFB] sm:grid-cols-[280px_1fr] sm:gap-8 reveal ${
                        index ? `reveal-delay-${Math.min(index * 50, 300)}` : ""
                      }`}
                    >
                      <span className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1F8483]">
                        {item.label}
                      </span>
                      <span className="text-base text-[#2F3B3A]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Objectives Section - Unique card design */}
        <section className="bg-[#F6FBFA] py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-0">
            <div className="reveal" data-reveal="fade-up">
              <h2 className="text-3xl font-bold text-[#0D2D2A] sm:text-4xl">
                {content.objectives.title}
              </h2>
              
              <div className="mt-10 grid gap-6 md:grid-cols-2">
                {content.objectives.items.map((item, index) => (
                  <div
                    key={index}
                    className={`group relative overflow-hidden rounded-3xl border border-[#D5ECE6] bg-white p-8 shadow-[0_20px_45px_-40px_rgba(17,66,61,0.5)] transition-all duration-300 hover:-translate-y-1 hover:border-[#B8E4DC] hover:shadow-[0_28px_60px_-48px_rgba(17,66,61,0.55)] reveal ${
                      index ? `reveal-delay-${Math.min(index * 100, 300)}` : ""
                    }`}
                  >
                    {/* Decorative number */}
                    <span className="absolute -right-4 -top-4 text-[120px] font-black leading-none text-[#1F8483] opacity-[0.04] transition-transform duration-500 group-hover:scale-110">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    
                    <div className="relative">
                      <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#1F8483] text-white shadow-md">
                        <span className="text-sm font-bold">{index + 1}</span>
                      </div>
                      <p className="leading-relaxed text-[#2F3B3A]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Funding Section */}
        <section className="bg-[#F0FAF8] py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-0">
            <div className="reveal flex flex-col items-center text-center" data-reveal="fade-up">
              <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1F8483] text-white shadow-lg">
                <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#0D2D2A] sm:text-3xl">
                {content.funding.title}
              </h2>
              <p className="mt-3 max-w-xl text-lg text-[#2F3B3A]">
                {content.funding.description}
              </p>
              <a
                href={content.funding.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#1F8483] px-8 py-4 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-[#176865] hover:shadow-xl"
              >
                {content.funding.linkLabel}
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* Funding logos bar: PRR, República Portuguesa, EU NextGeneration */}
        <section className="bg-white py-8 border-b border-[#E2E8F0]">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-0">
            <img
              src="/assets/apoiosC.png"
              alt={lang === "pt" 
                ? "Logótipos: PRR - Plano de Recuperação e Resiliência, República Portuguesa, União Europeia - NextGeneration EU" 
                : "Logos: PRR - Recovery and Resilience Plan, Portuguese Republic, European Union - NextGeneration EU"
              }
              className="w-full h-auto"
            />
          </div>
        </section>
      </main>
      <Footer lang={lang} variant="coaching40" />
    </>
  );
}
